import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Mail, MailOpen, ChevronRight } from 'lucide-react';
import type { Lead, LeadStatus } from '@inafana/shared';
import { api, ApiError } from '@/lib/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { formatDateTime } from '@/lib/utils';

const STATUS_VARIANTS: Record<LeadStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  NEW: 'default',
  CONTACTED: 'secondary',
  QUALIFIED: 'outline',
  REJECTED: 'destructive',
};

const STATUS_OPTIONS: LeadStatus[] = ['NEW', 'CONTACTED', 'QUALIFIED', 'REJECTED'];

export function Leads(): React.JSX.Element {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [openLead, setOpenLead] = useState<Lead | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'leads'],
    queryFn: () => api<{ leads: Lead[] }>('/admin/leads'),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<{ status: LeadStatus; isRead: boolean; notes: string | null }> }) =>
      api<{ lead: Lead }>(`/admin/leads/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: ({ lead }) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'leads'] });
      // Keep the dialog in sync if it's open on this lead.
      if (openLead && openLead.id === lead.id) setOpenLead(lead);
    },
    onError: (err) => {
      toast({
        title: 'Error',
        description: err instanceof ApiError ? err.message : 'Update failed',
        variant: 'destructive',
      });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/admin/leads/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'leads'] });
      setOpenLead(null);
    },
  });

  const openLeadAndMarkRead = (lead: Lead): void => {
    setOpenLead(lead);
    if (!lead.isRead) update.mutate({ id: lead.id, patch: { isRead: true } });
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Leads</h1>
        <p className="text-sm text-muted-foreground">
          Inbound leads from the public site's "get in touch" form.
        </p>
      </header>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12" />
              <TableHead>Lead</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Received</TableHead>
              <TableHead className="text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && (data?.leads.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No leads yet.
                </TableCell>
              </TableRow>
            )}
            {data?.leads.map((lead) => (
              <TableRow
                key={lead.id}
                className={lead.isRead ? '' : 'bg-accent/20'}
                onClick={() => openLeadAndMarkRead(lead)}
                style={{ cursor: 'pointer' }}
              >
                <TableCell>
                  {lead.isRead ? (
                    <MailOpen className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Badge>new</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="font-medium">{lead.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {lead.email}
                    {lead.phone ? ` · ${lead.phone}` : ''}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={STATUS_VARIANTS[lead.status]}>
                    {lead.status.toLowerCase()}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDateTime(lead.createdAt)}
                </TableCell>
                <TableCell className="text-right">
                  <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={openLead !== null} onOpenChange={(o) => !o && setOpenLead(null)}>
        {openLead && (
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{openLead.name}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Email</div>
                  <a className="text-primary hover:underline" href={`mailto:${openLead.email}`}>
                    {openLead.email}
                  </a>
                </div>
                {openLead.phone && (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Phone</div>
                    <a className="text-primary hover:underline" href={`tel:${openLead.phone}`}>
                      {openLead.phone}
                    </a>
                  </div>
                )}
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Received</div>
                  <div>{formatDateTime(openLead.createdAt)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Status</div>
                  <select
                    className="mt-1 block w-full rounded-md border bg-background px-2 py-1 text-sm"
                    value={openLead.status}
                    onChange={(e) =>
                      update.mutate({
                        id: openLead.id,
                        patch: { status: e.target.value as LeadStatus },
                      })
                    }
                    disabled={update.isPending}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s.toLowerCase()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Workflow / agent description
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm">{openLead.description}</p>
              </div>

              {openLead.customFields.length > 0 && (
                <div>
                  <div className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                    Custom fields
                  </div>
                  <dl className="rounded-md border">
                    {openLead.customFields.map((f, i) => (
                      <div
                        key={`${f.key}-${i}`}
                        className={`grid grid-cols-[180px_1fr] gap-3 p-3 text-sm ${i > 0 ? 'border-t' : ''}`}
                      >
                        <dt className="text-muted-foreground">{f.label}</dt>
                        <dd>{f.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              <div>
                <div className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                  Internal notes
                </div>
                <Textarea
                  rows={3}
                  defaultValue={openLead.notes ?? ''}
                  placeholder="Add notes (saved on blur)…"
                  onBlur={(e) => {
                    const next = e.target.value.trim();
                    const current = openLead.notes ?? '';
                    if (next === current) return;
                    update.mutate({
                      id: openLead.id,
                      patch: { notes: next === '' ? null : next },
                    });
                  }}
                />
              </div>
            </div>

            <DialogFooter className="flex justify-between sm:justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  update.mutate({
                    id: openLead.id,
                    patch: { isRead: !openLead.isRead },
                  })
                }
              >
                <Mail className="mr-2 h-4 w-4" />
                Mark {openLead.isRead ? 'unread' : 'read'}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  if (confirm(`Delete lead from ${openLead.name}?`)) remove.mutate(openLead.id);
                }}
                disabled={remove.isPending}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
